/* eslint-disable @next/next/no-img-element */
import React, { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { format } from "date-fns";
import es from "date-fns/locale/es/index.js";
import * as Text from "@/components/text";
import Metadata from "@/components/film/Metadata";
import Button from "../actions/Button";
import classNames from "classnames";
import PropTypes from "prop-types";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/outline";

const themes = {
  light: "bg-white text-neutral-900",
  dark: "bg-neutral-900 text-white",
};

const sizes = {
  xl: "w-[305px]",
  lg: "w-[232px]",
  md: "w-[189px]",
  sm: "w-[152px]",
};

function PosterCard({
  size = "lg",
  image = "https://images.unsplash.com/photo-1658686873732-c93680078088?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1287&q=80",
  theme = "dark",
  title = "",
  description = "",
  grid_details = false,
  tag = "Nota",
  date = "",
  slug = "",
}) {
  const [zoom, setZoom] = useState(false);
  const checkSlug = (s) => {
    if (s === null) {
      return "";
    }
    return s;
  };

  const [visible, toggleVisible] = useState(false);
  return (
    <Link href={slug}>
      <div className={classNames("w-full", "aspect-[3/2]")}>
        {/* On hover it toggles the visible state, which controls the zoom and the overlay */}
        <motion.div
          onMouseEnter={() => toggleVisible(true)}
          onMouseLeave={() => toggleVisible(false)}
          className="relative overflow-hidden bg-black"
        >
          {/* This is the poster card image, it zooms in on hover, visible state controls the zoom */}
          <motion.img
            alt="movie poster"
            className={`${"relative aspect-[3/2] h-full w-full cursor-pointer object-cover transition duration-500"} ${
              visible ? "scale-125" : "scale-100"
            }`}
            src={image}
          ></motion.img>
          <div className="absolute bottom-0 left-0 flex w-full justify-between bg-neutral-100">
            <span className="z-1 bg-black px-2 py-0.5 text-xs font-semibold text-white">
              {tag}
            </span>
            <span className="px-2 py-0.5 text-xs text-neutral-600">
              {format(new Date(date), "dd MMMM yyyy", { locale: null })}
            </span>
          </div>
        </motion.div>

        {/* Movie title */}
        <div
          className={classNames(
            themes[theme],
            `${
              grid_details ? "grid-rows-[0.3fr_0.2fr_0.1fr_0.1fr] " : ""
            } grid-cols-1 space-y-2 px-1 py-2 md:grid `
          )}
        >
          <div className="">
            <Text.ProperNoun
              className={`mr-2 line-clamp-2  gap-2 sm:text-[16px] lg:text-[20px] ${
                theme === "light" ? "text-neutral-800" : "text-neutral-100"
              } normal-case`}
              fontWeight="bold"
              as="span"
              size="md"
            >
              {title}
            </Text.ProperNoun>
          </div>

          {/* Movie directors */}
          <Text.Body
            as="div"
            className={`${
              theme === "light" ? "text-neutral-800" : "text-neutral-500"
            } last:after:content-[''] sm:text-[14px] lg:text-[16px]`}
            size="sm"
          >
            {description}
          </Text.Body>
          <div>
            <Link href={slug}>
              <Text.Body
                as="div"
                className="inline-flex items-center border-b-2 border-neutral-400 pb-1 text-sm font-bold text-neutral-900"
                size="sm"
              >
                Ir a la publicación <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Text.Body>
            </Link>
          </div>
        </div>
      </div>
    </Link>
  );
}

PosterCard.propTypes = {
  film_trailer_link: PropTypes.string,
  film_synopsis: PropTypes.string,
  film_streaming_link: PropTypes.string,
  film_ama: PropTypes.bool,
  theme: PropTypes.oneOf(["light", "dark"]),
  title: PropTypes.string,
  film_original_title: PropTypes.string,
  film_directors: PropTypes.array,
};

export default PosterCard;
